<template>
  <div
    id="app"
    :class="'logged-' + (userData ? 'in' : 'out') + (activeJournalCategory === null ? '' : ' journal-open')"
  >
    <template v-if="userData">
      <router-view />
      <journal />
    </template>
    <div
      v-else
      class="container"
    >
      <b-img
        v-if="client && client.logo"
        :src="client.logo"
        :alt="client.name"
        class="logo"
        style="max-width: 300px; max-height: 150px; object-fit: contain;"
      />
      <img
        v-else-if="!$route.params.client_slug"
        src="./assets/logo-2.png"
        alt="To Be Legacy"
        class="logo"
      >

      <b-nav
        pills
        align="center"
      >
        <b-nav-item
          :to="loginRoute"
          active-class="active"
        >
          Log In
        </b-nav-item>

        <b-nav-item
          v-if="!isStoreApp"
          :to="enrollRoute"
          active-class="active"
        >
          Enroll
        </b-nav-item>
      </b-nav>

      <router-view />
    </div>
  </div>
</template>

<script>
import Journal from '@/components/Journal.vue';
import { isStoreApp } from '@/utils/platform';

export default {
  name: 'App',
  components: {
    Journal,
  },
  computed: {
    isStoreApp,
    activeJournalCategory() {
      return this.$store.state.activeJournalCategory;
    },
    userData() {
      return this.$store.state.userData;
    },
    client() {
      return this.$store.state.client;
    },
    clientSlugFromRoute() {
      const { name } = this.$route;
      if (name === 'login' || name === 'enroll') {
        return null;
      }
      return this.$route.params.clientSlug ?? this.$route.params.client_slug ?? null;
    },
    loginRoute() {
      if (this.clientSlugFromRoute) {
        return { name: 'client-login', params: { client_slug: this.clientSlugFromRoute } };
      }
      return { name: 'login' };
    },
    enrollRoute() {
      if (this.clientSlugFromRoute) {
        return { name: 'client-enroll', params: { client_slug: this.clientSlugFromRoute } };
      }
      return { name: 'enroll' };
    },
  },
};
</script>

<style lang="scss">
@import './styles/breakpoints.scss';

// Color system
$primary: rgb(25, 132, 248);
$info: #21c3bd;
$gradient-dark-blue: #004d9a;
$gradient-teal: #2defda;

// Gradients
$teal-dark-blue: linear-gradient(0deg, #{$gradient-dark-blue} 0%, #{$gradient-teal} 100%);
$dark-blue-teal: linear-gradient(0deg, #{$gradient-teal} 0%, #{$gradient-dark-blue} 100%);

// Body
$body-color: #424140;

// Links
$link-color: #87cefc;
$link-decoration: underline;
$link-hover-color: $primary;
$link-hover-decoration: none;

// Typography
@font-face {
  font-family: 'Montserrat';
  src: local('Montserrat'),
    url(./assets/fonts/Montserrat/Montserrat-Regular.ttf) format('truetype');
}
$font-family-sans-serif: 'Montserrat', sans-serif;
$font-weight-base: 300;
$h1-font-size: 1.9rem;
$h2-font-size: 1.8rem;

// Buttons + Forms
$input-btn-padding-y-lg: 14px;
$input-btn-padding-x-lg: 40px;
$input-btn-font-size-lg: 14px;

// Buttons
$btn-border-width: 0;
$btn-font-weight: 600;

// Navbar
$navbar-nav-link-padding-x: 0.94rem;
$navbar-light-color: $body-color;
$navbar-light-hover-color: $primary;
$navbar-light-active-color: #fff;

// Dropdowns
$dropdown-font-size: 14px;

// Modals
$modal-inner-padding: 2.5rem;
$modal-content-bg: transparent;
$modal-content-border-width: 0;
$modal-backdrop-opacity: 0.85;
$modal-footer-border-width: 0;
$modal-header-padding-x: 0;

// Progress bars
$progress-height: 0.8rem;
$progress-border-radius: 0.5rem;

@import 'bootstrap/scss/bootstrap.scss';
@import 'bootstrap-vue/src/index.scss';

:root {
  --header-height: 111px;
  --bottom-bar-height: 80px;
  --sidebar-width: 268px;
  --app-gradient: linear-gradient(0deg, rgb(49, 207, 215) 0%, rgb(25, 132, 248) 100%);
  --safe-area-top: env(safe-area-inset-top, 0px);
  --safe-area-right: env(safe-area-inset-right, 0px);
  --safe-area-bottom: env(safe-area-inset-bottom, 0px);
  --safe-area-left: env(safe-area-inset-left, 0px);
  --landscape-notch-left: 0px;
  --landscape-notch-right: 0px;
  --mobile-header-chrome: 60px;
  --mobile-header-total: calc(var(--mobile-header-chrome) + var(--safe-area-top));
  --program-header-chrome: 56px;
  --program-header-total: calc(var(--program-header-chrome) + var(--safe-area-top));
}

html {
  min-height: 100vh;
  width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

body {
  min-height: 100vh;
  width: 100%;
  margin: 0;
  background-color: #fff;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

#app {
  min-height: 100vh;
  width: 100%;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

/* Hide scrollbars app-wide while keeping scroll */
main,
.content,
.logged-out {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

h1 {
  text-align: center;
  margin: 50px 0px;
  font-weight: bold;
}

a {
  transition: .1s all ease-in-out;
}

form {
  width: 430px;
  max-width: 100%;
  margin: 0 auto;
}

.alert p:last-child {
  margin-bottom: 0;
}

.btn {
  text-transform: uppercase;

  + .btn {
    margin-left: 1rem;
  }
}

.stripe-payment-form:after {
  content: '';
  display: block;
  height: 1rem;
}

.background-primary {
  background-color: rgb(25, 132, 248);
}

.background-gradient {
  background-image: var(--app-gradient);
}

.btn-primary {
  background-image: linear-gradient(90deg, rgb(49, 207, 215) 0%, rgb(25, 132, 248) 100%);

  &:not(:disabled) {
    &:focus,
    &:hover {
      background-image: linear-gradient(90deg, rgb(25, 132, 248) 0%, rgb(25, 132, 248) 100%);
    }
  }
}

.modal-backdrop {
  background-image: var(--app-gradient);
}

.modal-title {
  color: #fff;
  text-shadow: 0 1px 0 rgba(0, 0, 0, 0.15);
}

.modal-body {
  font-size: 0.875rem;
  background-color: #fff;
  border-radius: 3px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  word-break: break-word;

  p:last-child {
    margin-bottom: 0;
  }
}

.modal-footer {
  margin-top: -3px;
  padding: 0;
  justify-content: center;
}

.icon {
  width: 21px;

  .btn & {
    margin-left: -10px;
    margin-right: 5px;
    vertical-align: bottom;
  }
}

.profile {
  margin: 0 auto;
}

.sidebar {
  position: fixed;
  padding: 1rem 0;
  width: var(--sidebar-width);
  height: 100vh;
  overflow-y: auto;
  background: var(--primary-color, #{$teal-dark-blue});
  color: white;

  .nav .nav-item {
    cursor: pointer;

    .nav-link {
      color: white;
      font-weight: 500;
      text-decoration: none;

      &.active {
        color: var(--primary);
        font-weight: 600;
        background-color: white;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        position: relative;
      }

      &:hover {
        font-weight: 600;
      }
    }
  }
}

.mobile-navbar {
  display: none;

  .nav .nav-item {
    cursor: pointer;

    .nav-link {
      color: white;
      text-decoration: none;
      font-weight: 500;

      &.active {
        color: var(--primary);
        font-weight: 600;
        background-color: white;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
        position: relative;
      }

      &:hover {
        font-weight: 600;
      }
    }
  }
}

  .mobile-header {
    display: none !important;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    box-sizing: border-box;
    height: var(--mobile-header-total);
    padding-top: var(--safe-area-top);
    padding-right: max(15px, var(--safe-area-right));
    padding-left: max(15px, var(--safe-area-left));
    background: var(--app-gradient);
    color: white;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1050;
    display: flex;
    justify-content: space-between;
    align-items: center;

  .mobile-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }

  .back-button-mobile {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.1);
    border: none;
    border-radius: 8px;
    color: white;
    cursor: pointer;
    transition: background-color 0.2s;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .header-logo {
    text-decoration: none;
    display: flex;
    align-items: center;
    color: white;

    .client-logo {
      max-height: 40px;
      max-width: 120px;
      object-fit: contain;
    }
  }

  .header-profile {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    color: inherit;
    font: inherit;

    .profile-pic {
      width: 36px;
      height: 36px;
      object-fit: cover;
    }

    .profile-name {
      font-size: 14px;
      font-weight: 500;
      color: white;
      max-width: 80px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

  .bottom-nav-bar {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    min-height: var(--bottom-bar-height, 80px);
    height: calc(var(--bottom-bar-height, 80px) + env(safe-area-inset-bottom, 0));
    background-color: #fff;
    border-top: 1px solid #e9ecef;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
    z-index: 1060;
    justify-content: space-around;
    align-items: center;
    padding: 0 10px;
    padding-bottom: env(safe-area-inset-bottom, 0);

  .bottom-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: #6c757d;
    min-height: 50px;
    min-width: 50px;
    border-radius: 8px;
    transition: all 0.2s ease;
    padding: 2px;
    position: relative;

    // Ensure minimum touch target of 44px
    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      min-width: 44px;
      min-height: 44px;
    }

    &.active {
      color: var(--primary-color, #198cf8);
      background-color: rgba(25, 140, 248, 0.1);

      .nav-icon svg {
        fill: currentColor;
      }
    }

    &:hover, &:focus {
      color: var(--primary-color, #198cf8);
      background-color: rgba(25, 140, 248, 0.05);
      outline: none;
    }

    .nav-icon {
      margin-bottom: 2px;

      svg {
        width: 24px;
        height: 24px;
        fill: none;
        stroke: currentColor;
        stroke-width: 2;
        stroke-linecap: round;
        stroke-linejoin: round;
      }
    }

    .nav-label {
      font-size: 11px;
      font-weight: 500;
      text-align: center;
      line-height: 1;
    }
  }
}

.main-header {
  position: sticky;
  top: 0;
  display: grid;
  height: var(--header-height);
  background-color: #fff;
  border-bottom: 1px solid $border-color;
  z-index: 1;

  .navbar {
    margin-left: auto;
    padding: 0;
  }

  .navbar-nav {
    .dropdown-toggle {
      height: 100%;
      text-decoration: none;
      font-size: 0.87rem;
      font-weight: 400;

      &:focus,
      &:hover {
        fill: $primary;
      }
    }

    .icon {
      margin-right: 10px;
      transition: .1s fill ease-in-out;
    }

    .dropdown-menu {
      margin: 0;
      min-width: 100%;

      &:before {
        content: '';
        position: absolute;
        top: -7px;
        left: 50%;
        transform: translateX(-50%);
        display: block;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 7px 7px 7px;
        border-color: transparent transparent #fff transparent;
      }
    }

    .dropdown-item {
      text-decoration: none;
    }
  }

  .nav-item.show {
    .dropdown-toggle {
      background-image: linear-gradient(0deg, rgba(135,206,252,1) 0%, rgba(111,147,227,1) 100%);
    }

    .icon {
      fill: $navbar-light-active-color;
    }
  }

  .nav-link {
    display: flex;
    align-items: center;
  }

  .profile-img {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    margin-right: 10px;
    border-radius: 50%;
    display: inline-block;
    overflow: hidden;
    border: 2px solid $body-color;

    &.client-logo {
      border-radius: 0;
      border: none;
      object-fit: contain;
      width: auto;
      max-width: 100px;
      background-color: transparent;
    }
  }

  .first-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.logged-out {
  display: grid;
  align-items: center;
  padding: max(60px, calc(15px + env(safe-area-inset-top, 59px))) 0 15px;
  min-height: 100vh;
  min-height: 100dvh;
  height: 100%;
  background-color: #f5f5f5;

  .container {
    max-width: 100vw;
    width: 100%;
    background-color: #f5f5f5;
  }

  .logo {
    display: block;
    margin: 0 auto 1.5rem;
    width: 230px;
  }

  .nav {
    margin-bottom: 1.5rem;
  }

  .nav-item + .nav-item {
    margin-left: 1rem;
  }

  .nav-link {
    text-decoration: none;
  }
}

.logged-in {
  .logo {
    margin: 1.5rem 15px;
    width: 130px;
  }
}

.content {
  @include make-container();
  padding-top: 15px;
  padding-bottom: 15px;
}

.video-container {
  position: relative;
  margin: 1rem 0;
  padding-bottom: 56.25%;
  max-width: 100%;
  height: 0;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
}

@media (min-width: 1024px) {
  .main-header {
    grid-template-columns: auto 1fr;

    .navbar-nav {
      height: 100%;
    }

    .nav-item-user {
      min-width: 9.5rem;
      max-width: 12rem;
    }
  }

  main {
    margin-left: var(--sidebar-width);
  }
}

@media (max-width: 1199.98px) {
  .btn .icon {
    margin-left: 0;
    margin-right: -3px;
  }
}

@media (max-width: 1023.98px) {
  :root {
    --header-height: 150px;
  }

  .sidebar {
    display: none;
  }

  .mobile-navbar {
    display: block;
  }

  .main-header {
    grid-template-rows: 1fr auto;

    .navbar,
    .navbar-nav {
      width: 100%;
    }

    .nav-item {
      width: 50%;
    }

    .nav-link {
      justify-content: center;
    }
  }

  // Mobile-first improvements
  .container {
    padding-left: 15px;
    padding-right: 15px;
  }

  .logged-in {
    h1 {
      width: 100%;
      font-size: 1.4rem;
    }

    .logo {
      display: block;
      margin: 6px auto;
      width: 170px;
    }
  }

  // Better mobile scrolling
  body {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: none;
  }

  // Mobile-friendly modal improvements
  .modal-dialog {
    margin: env(safe-area-inset-top, 0) 15px env(safe-area-inset-bottom, 0) 15px;
  }
}

// Landscape on tablet-width viewports: use the left sidebar instead of bottom / stacked mobile chrome.
@media #{$landscape-sidebar} {
  :root {
    --sidebar-width: 168px;
  }

  .sidebar {
    display: block;
    left: 0;
    width: calc(var(--sidebar-width) + var(--landscape-notch-left));
    padding: max(0.5rem, env(safe-area-inset-top, 0px)) 8px 0.5rem var(--landscape-notch-left);
    box-sizing: border-box;

    .text-center {
      padding-left: 0;
      padding-right: 0;

      .p-3 {
        padding: 0.35rem !important;
      }

      img:not(.rounded-circle) {
        max-width: 100px !important;
        max-height: 48px !important;
        object-fit: contain;
      }

      .rounded-circle {
        width: 56px !important;
        height: 56px !important;
      }

      p {
        font-size: 0.75rem;
        line-height: 1.2;
        margin-bottom: 0.25rem;
      }
    }

    hr {
      margin: 0.4rem 0.75rem;
    }

    .nav-link {
      font-size: 0.78rem;
      padding: 0.3rem 0.65rem;
    }
  }

  .mobile-navbar,
  .mobile-header,
  .bottom-nav-bar {
    display: none !important;
  }

  html:has(.logged-in),
  body:has(.logged-in) {
    overflow: auto;
    position: static;
    height: auto;
    width: auto;
  }

  .logged-in .profile-wrapper {
    height: auto;
    overflow: visible;
  }

  main {
    margin-left: calc(var(--sidebar-width) + var(--landscape-notch-left));
    width: calc(100vw - var(--sidebar-width) - var(--landscape-notch-left));
    max-width: calc(100vw - var(--sidebar-width) - var(--landscape-notch-left));
    padding-right: var(--landscape-notch-right);
    margin-top: 0;
    margin-bottom: 0;
    padding-bottom: 0;
    box-sizing: border-box;
    overflow-y: auto;
  }

  .logged-in {
    h1 {
      margin: 0.75rem 0;
      font-size: 1.25rem;
    }
  }

  .content {
    padding-top: 12px;
    padding-bottom: 16px;
    padding-left: 12px;
    padding-right: 12px;
    max-width: 100%;
    box-sizing: border-box;
  }
}

// Bottom navigation bar only for small mobile devices (phones in portrait)

@media #{$compact-mobile} {
  :root {
    --bottom-bar-height: 80px;
  }

  .logged-out {
    background-color: #f5f5f5;
    // Thin brand strip behind the status bar only (not a full header band).
    background-image: var(--app-gradient);
    background-repeat: no-repeat;
    background-size: 100% var(--safe-area-top);
    padding-top: max(1rem, calc(var(--safe-area-top) + 0.5rem));
  }

  // Lock document scroll so content cannot scroll behind header/status bar
  html:has(.logged-in),
  body:has(.logged-in) {
    overflow: hidden;
    height: 100%;
    position: fixed;
    width: 100%;
  }

  .logged-in .profile-wrapper {
    height: 100vh;
    height: 100dvh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }

  .mobile-header {
    display: flex !important;
  }

  .bottom-nav-bar {
    display: flex;
  }

  body:has(#app.journal-open) {
    .mobile-header {
      display: none !important;
    }

    .bottom-nav-bar {
      display: none !important;
    }
  }

  main {
    flex: 1;
    min-height: 0;
    margin-top: var(--mobile-header-total);
    margin-bottom: calc(var(--bottom-bar-height, 80px) + env(safe-area-inset-bottom, 0));
    padding-bottom: env(safe-area-inset-bottom, 0);
    overflow-y: auto;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;
  }

  .mobile-navbar {
    display: none;
  }

  // Mobile-first button improvements
  .btn {
    min-height: 44px;
    font-size: 16px; // Prevents zoom on iOS
    padding: 12px 24px;
  }

  // Better form inputs for mobile
  .form-control {
    min-height: 44px;
    font-size: 16px;
    border-radius: 8px;
  }

  // Improved spacing for mobile content
  .content {
    padding: 20px 15px;
    padding-bottom: calc(var(--bottom-bar-height, 80px) + env(safe-area-inset-bottom, 0) + 60px);
  }

  // Logout page: fill main so content cannot be scrolled off (no overflow = no scroll)
  .content.logout-page {
    min-height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  // Better mobile navigation spacing
  .bottom-nav-bar {
    padding: 6px max(5px, var(--safe-area-right)) env(safe-area-inset-bottom, 0) max(5px, var(--safe-area-left));

    .bottom-nav-item {
      min-width: 48px;
      min-height: 48px;
      padding: 2px;
    }
  }
}

@media (max-width: 374.98px) {
  .main-header {
    .nav-item:first-child {
      width: 45%;
    }

    .nav-item-user {
      width: 55%;
      max-width: 12rem;
    }
  }

  :root {
    --mobile-header-chrome: 50px;
  }

  // Very small mobile screens
  .mobile-header {
    padding-right: 12px;
    padding-left: 12px;

    .header-logo .client-logo {
      max-height: 32px;
    }

    .mobile-header-left {
      gap: 8px;
    }

    .back-button-mobile {
      width: 36px;
      height: 36px;

      svg {
        width: 18px;
        height: 18px;
      }
    }

    .header-logo .client-logo {
      max-height: 32px;
      max-width: 100px;
    }

    .header-profile {
      gap: 6px;

      .profile-pic {
        width: 32px;
        height: 32px;
      }

      .profile-name {
        font-size: 13px;
        max-width: 70px;
      }
    }
  }

  main {
    flex: 1;
    min-height: 0;
    margin-top: var(--mobile-header-total);
    margin-bottom: calc(70px + env(safe-area-inset-bottom, 0));
    padding-bottom: env(safe-area-inset-bottom, 0);
    overflow-y: auto;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: touch;
  }

  .content.logout-page {
    min-height: 100%;
  }

  .bottom-nav-bar {
    height: calc(70px + env(safe-area-inset-bottom, 0));
    padding: 4px 2px env(safe-area-inset-bottom, 0) 2px;

    .bottom-nav-item {
      min-width: 44px;
      min-height: 44px;
      padding: 1px;

      .nav-icon svg {
        width: 20px;
        height: 20px;
      }

      .nav-label {
        font-size: 10px;
      }

      .dropdown {
        .dropdown-toggle {
          background: none !important;
          border: none !important;
          padding: 0 !important;
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          color: inherit !important;
          font: inherit;
          cursor: pointer;

          &::after {
            content: '';
            width: 0;
            height: 0;
            border-left: 3px solid transparent;
            border-right: 3px solid transparent;
            border-top: 3px solid currentColor;
            margin-top: 2px;
            position: absolute;
            bottom: 8px;
            right: 50%;
            transform: translateX(50%);
          }

          &:hover, &:focus {
            background: none !important;
          }
        }

        .dropdown-menu {
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
          padding: 8px 0;
          min-width: 120px;
          z-index: 1060;
          border: none;

          .dropdown-item {
            padding: 8px 16px;
            color: #333;
            text-decoration: none;
            display: block;
            font-size: 14px;

            &:hover, &:focus, &.active {
              background-color: rgba(25, 140, 248, 0.1) !important;
              color: var(--primary-color, #198cf8) !important;
            }
          }
        }
      }
    }
  }
}


</style>
